import { useState, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { addMonths, startOfMonth } from 'date-fns';
import { WorkstreamList } from './WorkstreamList';
import { WorkstreamDialog } from './WorkstreamDialog';
import FloatingActionButton from './FloatingActionButton';
import { Legend } from './Legend';
import { Workstream, WorkstreamFormData, Milestone, Assignee } from './types';
import TimelineControls from './TimelineControls';
import { TimelineView } from './utils/timelineUtils';
import SlidePanel from './SlidePanel';

interface WorkspaceContentProps {
  workstreams: Workstream[];
  isLoading: boolean;
  assignees: Assignee[];
  onAdd: (data: WorkstreamFormData) => Promise<any>;
  onUpdate: (id: string, data: Partial<WorkstreamFormData>) => Promise<any>;
  onDelete: (id: string) => Promise<void>;
  onUpdateMilestones: (workstreamId: string, milestones: Milestone[]) => Promise<void>;
  showAddDialog: boolean;
  onAddDialogClose: () => void;
}

export const WorkspaceContent: React.FC<WorkspaceContentProps> = ({
  workstreams,
  isLoading,
  assignees,
  onAdd,
  onUpdate,
  onDelete,
  onUpdateMilestones,
  showAddDialog,
  onAddDialogClose
}) => {
  const [editingWorkstream, setEditingWorkstream] = useState<Workstream | undefined>(undefined);
  const [timelineView, setTimelineView] = useState<TimelineView>('month');
  const [zoomLevel, setZoomLevel] = useState(1);
  const [timelineStart, setTimelineStart] = useState(() => startOfMonth(new Date()));
  const [timelineEnd, setTimelineEnd] = useState(() => startOfMonth(addMonths(new Date(), 12)));

  // Debug logging for timeline configuration
  useEffect(() => {
    console.log('Timeline Configuration:', {
      view: timelineView,
      zoomLevel,
      startDate: timelineStart.toISOString(),
      endDate: timelineEnd.toISOString()
    });
  }, [timelineView, zoomLevel, timelineStart, timelineEnd]);

  const handleAdd = async (data: WorkstreamFormData) => {
    await onAdd(data);
    onAddDialogClose();
  };

  const handleEdit = async (data: WorkstreamFormData) => {
    if (!editingWorkstream) return;
    
    const formData: Partial<WorkstreamFormData> = {
      startDate: new Date(data.startDate),
      endDate: new Date(data.endDate),
      type: data.type,
      status: data.status,
      parentId: data.parentId,
      isParent: data.isParent,
      assigneeId: data.assigneeId
    };

    if (data.description) {
      formData.description = data.description;
    }
    
    await onUpdate(editingWorkstream.id, formData);
    setEditingWorkstream(undefined);
  };

  const handleStartEdit = (workstream: Workstream) => {
    setEditingWorkstream(workstream);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      position: 'relative', 
      width: '100%', 
      height: '100%',
      overflow: 'hidden' // Prevent scrollbar issues
    }}>
      <TimelineControls
        view={timelineView}
        onViewChange={setTimelineView}
        zoomLevel={zoomLevel}
        onZoomChange={setZoomLevel}
        startDate={timelineStart}
        endDate={timelineEnd}
        onDateRangeChange={(start: Date, end: Date) => {
          console.log('Date Range Changed:', { start: start.toISOString(), end: end.toISOString() });
          setTimelineStart(start);
          setTimelineEnd(end);
        }}
      />
      <Legend />
      <WorkstreamList
        workstreams={workstreams}
        timelineConfig={{
          view: timelineView,
          zoomLevel,
          startDate: timelineStart,
          endDate: timelineEnd
        }}
        today={timelineStart}
        assignees={assignees}
        onDelete={onDelete}
        onEdit={handleStartEdit}
        onUpdate={onUpdate}
        onUpdateMilestones={onUpdateMilestones}
      />
<SlidePanel
  open={showAddDialog || !!editingWorkstream}
  onClose={() => {
    if (editingWorkstream) {
      setEditingWorkstream(undefined);
    } else {
      onAddDialogClose();
    }
  }}
>
  <WorkstreamDialog
    onSubmit={editingWorkstream ? handleEdit : handleAdd}
    initialData={editingWorkstream || undefined}  // Explicitly handle undefined
    mode={editingWorkstream ? 'edit' : 'add'}
    parentWorkstreams={workstreams.filter(w => w.isParent)}
    assignees={assignees}
  />
</SlidePanel>
      <FloatingActionButton 
        onClick={() => {
          if (!editingWorkstream) {
            onAddDialogClose();
          }
        }} 
      />
    </Box>
  );
};